import React, { isValidElement } from "react";
import ReactMarkdown, { Options } from "react-markdown";
import type { Pluggable } from "unified";
import { useTheme, Box } from "@mui/material";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { AuthenticatedImage } from "../authenticated-media/authenticated-image";
import { markdownSanitizeSchema } from "../../utils/markdownSanitizeSchema";

/**
 * Full HTML documents (`<!doctype>` / `<html>`) break in rehype-raw (table ends up
 * inside a broken `<style>`).
 */
const unwrapHtmlDocumentForMarkdown = (md: string): string => {
  const leadingWs = md.match(/^\s*/)?.[0] ?? "";
  const start = md.slice(leadingWs.length);
  if (!/^<!doctype\s+html/i.test(start) && !/^<html[\s>]/i.test(start)) {
    return md;
  }

  const htmlClose = start.search(/<\/html>/i);
  const doc = htmlClose >= 0 ? start.slice(0, htmlClose + "</html>".length) : start;
  const rest = htmlClose >= 0 ? start.slice(htmlClose + "</html>".length) : "";

  const styles = Array.from(doc.matchAll(/<style\b[^>]*>[\s\S]*?<\/style>/gi))
    .map((match) => match[0])
    .join("");
  const body = doc.match(/<body\b[^>]*>([\s\S]*?)<\/body>/i)?.[1]?.trim() ?? "";

  return `${leadingWs}${styles}\n${body}${rest}`;
};

const normalizeMarkdownChildren = (value: unknown): string => {
  if (typeof value === "string") return unwrapHtmlDocumentForMarkdown(value);
  if (value == null) return "";

  // react-markdown expects a single string; streaming / integrations can accidentally pass arrays
  if (Array.isArray(value)) {
    return value.map(normalizeMarkdownChildren).join("");
  }

  if (isValidElement(value)) {
    return "";
  }

  if (value instanceof String) return value.toString();

  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }

  return String(value);
};

export const CustomMarkdown = ({
  components,
  children,
  skipHtml,
  ...props
}: Readonly<Options>) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const textColor = isDark ? theme.palette.common.white : undefined;
  const normalizedChildren = normalizeMarkdownChildren(children);

  const rehypePlugins: Pluggable[] = skipHtml
    ? []
    : [rehypeRaw, [rehypeSanitize, markdownSanitizeSchema]];

  const borderColor = isDark ? "#444" : "#ddd";
  const thBg = isDark ? "#333" : "#f2f2f2";

  return (
    <Box
      className="markdown"
      sx={{
        color: textColor,
        overflowX: { xs: "auto", sm: "auto", md: "auto", lg: "visible" },
        WebkitOverflowScrolling: "touch",
        "& p": {
          color: textColor,
          margin: 0,
        },
        "& span": {
          color: textColor,
        },
        "& strong": {
          color: textColor,
        },
        "& em": {
          color: textColor,
        },
        "& ul, & ol": {
          color: textColor,
        },
        "& li": {
          color: textColor,
        },
        "& hr": {
          border: "none",
          borderTop: isDark
            ? "2px solid rgba(255, 255, 255, 0.35)"
            : "2px solid rgba(0, 0, 0, 0.28)",
          margin: "16px 0",
          width: "100%",
        },
        "& table": {
          width: { xs: "max-content", sm: "max-content", md: "auto", lg: "100%" },
          maxWidth: "100%",
          borderCollapse: "collapse",
          marginTop: "10px",
          border: `1px solid ${borderColor}`,
        },
        "& table.csu-otg-table, & .csu-matrix table": {
          width: "100%",
          minWidth: 0,
          maxWidth: "100%",
          tableLayout: "fixed",
          border: "none",
          marginTop: 0,
        },
        "& th, & td": {
          padding: "8px",
          textAlign: "left",
          border: `1px solid ${borderColor}`,
          color: textColor,
        },
        "& th": {
          backgroundColor: thBg,
          fontWeight: "bold",
        },
        // CSU heatmap: wrapper is <section class="csu-matrix">.
        // Do not inherit chat theme white / gray th background onto pastel cells.
        "& .csu-matrix": {
          color: "#10233f",
          backgroundColor: "#fff",
        },
        "& .csu-matrix th, & .csu-matrix td": {
          padding: "10px",
          border: "none",
          borderTop: "1px solid #d6e0ea",
          borderRight: "1px solid #d6e0ea",
          backgroundColor: "transparent",
          fontWeight: "inherit",
          color: "#10233f",
          textAlign: "center",
          verticalAlign: "middle",
        },
        "& .csu-matrix thead th": {
          backgroundColor: "#10325a",
          color: "#ffffff",
          fontWeight: 700,
        },
        "& .csu-matrix thead th *": {
          color: "#ffffff",
        },
        "& .csu-matrix tbody th": {
          backgroundColor: "#f7f9fc",
          color: "#10233f",
          textAlign: "left",
          fontWeight: 700,
        },
        "& .csu-matrix tbody th > span": {
          color: "#10233f",
          display: "block",
          fontWeight: 700,
        },
        "& .csu-matrix tbody th small": {
          display: "block",
          marginTop: "4px",
          color: "#52677f",
          fontSize: "11px",
          fontWeight: 400,
        },
        "& .csu-matrix .priority-0": { backgroundColor: "#f4f7fa" },
        "& .csu-matrix .priority-1": { backgroundColor: "#f8d9de" },
        "& .csu-matrix .priority-2": { backgroundColor: "#fff0c2" },
        "& .csu-matrix .priority-3": { backgroundColor: "#e5f3ec" },
        "& .csu-matrix td strong, & .csu-matrix td b": {
          display: "block",
          color: "#10233f",
        },
        "& .csu-matrix td span": {
          display: "block",
          marginTop: "3px",
          color: "#39506a",
          fontSize: "11px",
        },
        "& .csu-matrix td small": {
          display: "block",
          marginTop: "3px",
          padding: 0,
          border: "none",
          borderRadius: 0,
          width: "auto",
          color: "#52677f",
          backgroundColor: "transparent",
          fontSize: "11px",
          fontWeight: 600,
        },
        "& .csu-matrix__title": {
          margin: 0,
          color: "#10233f",
        },
        "& .csu-matrix__note": {
          margin: "5px 0 0",
          color: "#52677f",
          fontSize: "13px",
        },
        "& .csu-matrix__legend": {
          display: "flex",
          flexWrap: "wrap",
          gap: "10px",
          alignItems: "center",
          padding: "12px 18px",
          color: "#52677f",
          fontSize: "12px",
        },
        "& .csu-matrix__legend strong": {
          color: "#10233f",
          fontWeight: 700,
        },
        "& .csu-matrix__key": {
          display: "inline-flex",
          gap: "5px",
          alignItems: "center",
          color: "#52677f",
        },
        "& .csu-matrix__swatch": {
          display: "inline-block",
          width: "14px",
          height: "14px",
          border: "1px solid #b9c6d3",
          borderRadius: "3px",
          flexShrink: 0,
        },
        "& .csu-matrix__swatch--1": { backgroundColor: "#f8d9de" },
        "& .csu-matrix__swatch--2": { backgroundColor: "#fff0c2" },
        "& .csu-matrix__swatch--3": { backgroundColor: "#e5f3ec" },
        ...(isDark && {
          "& tr:nth-of-type(even)": {
            backgroundColor: "#2b2b2b",
          },
          "& .csu-matrix tr:nth-of-type(even)": {
            backgroundColor: "transparent",
          },
        }),
      }}
    >
      <ReactMarkdown
        skipHtml={skipHtml}
        rehypePlugins={rehypePlugins}
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={
          components ?? {
            p: ({ children, ...props }) => (
              <p style={{ color: textColor, margin: 0 }} {...props}>
                {children}
              </p>
            ),
            a: ({ className, children, ...props }) => (
              <a
                {...props}
                className={`underline text-blue-600 hover:text-blue-800 visited:text-purple-600 active:text-red-600 ${isDark ? "dark" : "light"} markdown ${className}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#FF7961",
                }}
              >
                {children}
              </a>
            ),
            pre: ({ style, children, ...props }) => (
              <pre
                style={{
                  background: "rgba(0, 0, 0, 0.2)",
                  borderRadius: "3px",
                  borderBottom: `3px solid ${theme.palette.buttons.textColor}`,
                  padding: "3px",
                  display: "inline-block",
                  maxWidth: "100%",
                  overflowX: "auto",
                  color: textColor,
                  ...style,
                }}
                {...props}
              >
                {children}
              </pre>
            ),
            img: ({ src, alt, style, ...props }) => {
              const imageSrc = src ?? "";
              const isLoaderGif = /(?:^|\/)(?:animation)?loading2\.gif(?:$|\?)/i.test(
                imageSrc,
              );

              return (
                <AuthenticatedImage
                  src={src}
                  alt={alt || "Image"}
                  sx={{
                    maxWidth: isLoaderGif
                      ? { xs: "140px", sm: "160px", md: "180px" }
                      : "100%",
                    height: "auto",
                    borderRadius: 1,
                    marginTop: 1,
                    marginBottom: 1,
                    display: "block",
                    maxHeight: isLoaderGif
                      ? { xs: "140px", sm: "160px", md: "180px" }
                      : { xs: "300px", md: "400px" },
                    mx: isLoaderGif ? "auto" : 0,
                    objectFit: "contain",
                    ...(isLoaderGif
                      ? {
                          width: undefined,
                          maxWidth: undefined,
                        }
                      : {}),
                    ...(style as React.CSSProperties | undefined),
                  }}
                  {...props}
                />
              );
            },
            br: () => <br />,
          }
        }
        {...props}
      >
        {normalizedChildren}
      </ReactMarkdown>
    </Box>
  );
};
